import React, { useContext } from 'react';
import {
  StyledLoginFormContainer,
  StyledInput,
  StyledInputAccessCode,
  StyledError,
  StyledErrorContainer,
  StyledHeader,
  StyledIcon,
  StyledLabel,
  StyledCheckBoxText,
  StyledExplanation,
  StyledButton,
  StyledCheckBoxContainer,
  StyledSpace,
  StyledTooltip,
  StyledPLeft,
  StyledInputCheckBox,
} from '../../../components/Styled/StyledLoginForm';
import { StyledTermsOfUseButton } from '../../../components/Styled/StyledButtons';
import { Tooltip, Form, EnumTooltipPosition, Hyperlink } from 'react-magma-dom';
import { useNavigate } from '@reach/router';
import { AxiosError } from 'axios';
import { HelpIcon } from 'react-magma-icons';
import { ErrorIcon } from 'react-magma-icons';
import accesscode from '../../../assets/accesscode.png';
import { useForm } from 'react-hook-form';
import { COLORS } from '../../../constants';
import { LoginRequest, TokenDecoded } from '../../../types';
import AuthContext from '../../../contexts/authContext';
import jwt_decode from 'jwt-decode';
import { authAPI } from '../../../API/auth';
import { getDefaultStage } from '../../../utils/storage';

type FormData = {
  accessCode: string;
  schoolEmail: string;
  licence: boolean;
};

interface ILoginForm {
  setIsTermsOfUseModalOpened: (isOpened: boolean) => void;
}
interface ErrorResponse {
  errors: {
    accessCode: string[];
  };
}
export const LoginForm = (props: ILoginForm): JSX.Element => {
  const { setIsTermsOfUseModalOpened } = props;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const { setAuth, setEmail, setDefaultStage, setAccess, setTrialDaysLeft } = useContext(AuthContext);
  const navigate = useNavigate();

  const tooltipContentLong = (
    <StyledTooltip>
      <StyledPLeft>Access code can be found on the inside cover of your BIG BOOK.</StyledPLeft>
      <StyledPLeft>
        Contact{' '}
        <Hyperlink target="_blank" to="https://cengage.com.au/contact-us">
          Cengage Support{' '}
        </Hyperlink>{' '}
        if you are having trouble.
      </StyledPLeft>
      <img src={accesscode as string} alt="accessCode" />
    </StyledTooltip>
  );

  const helperMessageExplanation = (
    <StyledExplanation>
      By entering your school email, you ensure the ability to recover your Access Code if you misplace your Big Book
    </StyledExplanation>
  );

  const focusedColor = COLORS.seaBlue;
  const errorColor = COLORS.venetianRed;
  const defaultColor = COLORS.veryLightGrey;

  const registerOptions = {
    accessCode: {
      required: (message = '') => (
        <StyledErrorContainer>
          <ErrorIcon id="ErrorBlackIcon" title="Error ACCESS CODE INVALID" size={17} />{' '}
          <StyledError>{message || 'Access code invalid'}</StyledError>
        </StyledErrorContainer>
      ),
    },
    schoolEmail: {
      required: (
        <StyledErrorContainer>
          <ErrorIcon id="ErrorBlackIcon" title="Error Email INVALID" size={17} />{' '}
          <StyledError>EMAIL INVALID</StyledError>
        </StyledErrorContainer>
      ),
    },
    licence: {
      required: '',
    },
  };

  const login = async (data: FormData): Promise<void> => {
    const loginDetails: LoginRequest = {
      email: data.schoolEmail,
      accessCode: data.accessCode,
      agreement: data.licence,
    };
    try {
      const { data } = await authAPI.login(loginDetails);

      const { accessToken } = data;
      setAuth(accessToken !== '');

      const { email, defaultStage, access, trialDaysLeft } = jwt_decode<TokenDecoded>(accessToken);
      setEmail(email);

      const stage = getDefaultStage() ? (getDefaultStage() as string) : defaultStage;
      setDefaultStage(stage);
      setAccess(access);
      setTrialDaysLeft(trialDaysLeft);

      const path = (localStorage.getItem('path') || '/') + (localStorage.getItem('hash') || '');
      navigate(path);
      localStorage.setItem('path', '/');
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>; // Cast to AxiosError with the defined error response type
      setError('accessCode', {
        type: 'manual',
        message: axiosError?.response?.data?.errors?.accessCode[0], // Accessing the error message safely
      });
    }
  };
  return (
    <StyledLoginFormContainer>
      <StyledHeader>Welcome</StyledHeader>
      <Form
        role="form"
        aria-label="Login Form"
        header=" "
        actions={
          <StyledButton onClick={handleSubmit(async data => await login(data))} disabled={!isValid}>
            Enter
          </StyledButton>
        }
      >
        <StyledLabel>
          Please enter your PM Writing Digital Platform <b> Access Code</b>
          <Tooltip content={tooltipContentLong} isInverse position={EnumTooltipPosition.right}>
            <StyledIcon>
              <HelpIcon size={17} id="helpIcon" title="Help Information" />
            </StyledIcon>
          </Tooltip>
        </StyledLabel>
        <label htmlFor="accessCode">
          <StyledInputAccessCode
            role="textbox"
            aria-label="accessCode"
            key="0"
            id="accessCode"
            type="text"
            {...register('accessCode', {
              required: true,
              maxLength: 14,
              minLength: 14,
            })}
            borderColor={errors?.accessCode ? errorColor : defaultColor}
            color={errors?.accessCode ? errorColor : focusedColor}
            focusColor={errors?.accessCode ? errorColor : focusedColor}
          />
        </label>
        {errors?.accessCode ? registerOptions.accessCode.required(errors?.accessCode?.message) : <StyledSpace />}
        <StyledLabel>
          and your <b> School Email</b>
        </StyledLabel>
        <label htmlFor="schoolEmail">
          <StyledInput
            role="textbox"
            id="inspectletIgnore"
            aria-label="schoolEmail"
            key="1"
            type="email"
            {...register('schoolEmail', {
              required: true,
              pattern:
                /(?!(^[.-].*|[^@]*[.-]@|.*\.{2,}.*)|^.{254}.)([a-zA-Z0-9!#$%&'*+=?^_`{|}~.-]+@)(?!-.*|.*-\.)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,15}/,
            })}
            borderColor={errors?.schoolEmail ? errorColor : defaultColor}
            color={errors?.schoolEmail ? errorColor : focusedColor}
            focusColor={errors?.schoolEmail ? errorColor : focusedColor}
          />
        </label>
        {errors?.schoolEmail ? registerOptions.schoolEmail.required : helperMessageExplanation}
        <StyledCheckBoxContainer>
          <StyledInputCheckBox
            role="checkbox"
            aria-checked="false"
            key="2"
            type="checkbox"
            aria-label="checkbox for licence"
            {...register('licence', { required: true })}
          />
          <StyledCheckBoxText>
            Please read the{' '}
            <StyledTermsOfUseButton
              type="button"
              onClick={() => {
                setIsTermsOfUseModalOpened(true);
              }}
            >
              terms of use
            </StyledTermsOfUseButton>
            . By using the PM Writing software, you agree to be bound by the terms of this licence.
          </StyledCheckBoxText>
        </StyledCheckBoxContainer>
      </Form>
    </StyledLoginFormContainer>
  );
};
